import type { DeadlineStep } from "../deadline";
import {
    BookingResolutionOfferingCategorySchema,
    type ChangeRequestStatusRequest,
    type RequestBookingOutcome,
    type RequestDeadlineStep,
    type RequestOutcome,
    type RequestStatus,
} from "../openapi/lmt/generated/lmt-api-types.gen";
import type { Request } from "./request";

export type RequestStatusBookingContext = {
    hasConfirmedBookings: boolean;
    hasCancelledBookings: boolean;
};

export type SetNextStepIntent = "update" | "confirm" | "reopen";

export type ChangeRequestStatusAction =
    | "set_next_step"
    | "confirm_no_further_action"
    | "close"
    | "change_booking_resolution"
    | "change_closed_resolution";

/** Deadline steps offered while the request is draft/new/open (or after reopen). */
export const OPEN_REQUEST_DEADLINE_STEPS = [
    "send_status_update",
    "send_proposal",
    "send_booking_confirmation",
    "wait_for_member_response",
    "wait_for_supplier_response",
] as const satisfies readonly DeadlineStep[];

/** Deadline steps offered while confirming or updating a confirmed request. */
export const CONFIRMED_REQUEST_DEADLINE_STEPS = [
    "no_further_action",
    "revisit_request",
    "send_status_update_internal",
    "check_on_pending_payment",
] as const satisfies readonly DeadlineStep[];

/** Confirmed-flow steps that map to `set_next_step` (not `confirm_no_further_action`). */
export const CONFIRMED_SET_NEXT_STEP_DEADLINE_STEPS =
    CONFIRMED_REQUEST_DEADLINE_STEPS.filter(
        (
            step,
        ): step is Exclude<
            (typeof CONFIRMED_REQUEST_DEADLINE_STEPS)[number],
            "no_further_action"
        > => step !== "no_further_action",
    );

export type OpenRequestDeadlineStep = (typeof OPEN_REQUEST_DEADLINE_STEPS)[number];
export type ConfirmedRequestDeadlineStep =
    (typeof CONFIRMED_REQUEST_DEADLINE_STEPS)[number];

export function requiresBookingResolution(
    offeringCategory: string | null | undefined,
): boolean {
    return BookingResolutionOfferingCategorySchema.safeParse(offeringCategory).success;
}

export function resolveStatusForSetNextStepIntent(
    intent: SetNextStepIntent,
    currentStatus: RequestStatus,
): RequestStatus {
    switch (intent) {
        case "reopen":
            return "open";
        case "confirm":
            return "confirmed";
        case "update":
            return currentStatus === "confirmed" ? "confirmed" : "open";
        default: {
            const _exhaustive: never = intent;
            return _exhaustive;
        }
    }
}

export type IsStatusActionAllowedParams = {
    action: ChangeRequestStatusAction;
    currentStatus: RequestStatus;
    bookings: RequestStatusBookingContext;
    intent?: SetNextStepIntent;
    /** When set, also validates the deadline step allowlist for the action/intent. */
    nextDeadlineStep?: DeadlineStep | string | null;
};

/**
 * Whether a change-request-status API action is allowed for the current request
 * status and booking flags. Mirrors the LMT status-selector top-level menu rules.
 */
export function isStatusActionAllowed(params: IsStatusActionAllowedParams): boolean {
    const { action, currentStatus, bookings, intent, nextDeadlineStep } = params;

    switch (action) {
        case "close":
            if (bookings.hasConfirmedBookings) {
                return false;
            }
            return currentStatus === "open" || currentStatus === "confirmed";
        case "change_booking_resolution":
            return currentStatus === "confirmed" && bookings.hasConfirmedBookings;
        case "change_closed_resolution":
            return (
                currentStatus === "closed" &&
                !bookings.hasConfirmedBookings &&
                !bookings.hasCancelledBookings
            );
        case "confirm_no_further_action":
            if (currentStatus === "confirmed") {
                return true;
            }
            return currentStatus === "open" && bookings.hasConfirmedBookings;
        case "set_next_step":
            if (intent == null) {
                return false;
            }
            return isSetNextStepAllowed(
                intent,
                currentStatus,
                bookings,
                nextDeadlineStep,
            );
        default: {
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}

function isSetNextStepAllowed(
    intent: SetNextStepIntent,
    currentStatus: RequestStatus,
    bookings: RequestStatusBookingContext,
    nextDeadlineStep: DeadlineStep | string | null | undefined,
): boolean {
    switch (intent) {
        case "update": {
            if (
                currentStatus === "draft" ||
                currentStatus === "new" ||
                currentStatus === "open"
            ) {
                return isAllowedDeadlineStep(
                    nextDeadlineStep,
                    OPEN_REQUEST_DEADLINE_STEPS,
                );
            }
            if (currentStatus === "confirmed") {
                return isAllowedDeadlineStep(
                    nextDeadlineStep,
                    CONFIRMED_SET_NEXT_STEP_DEADLINE_STEPS,
                );
            }
            return false;
        }
        case "confirm": {
            if (currentStatus !== "open" || !bookings.hasConfirmedBookings) {
                return false;
            }
            return isAllowedDeadlineStep(
                nextDeadlineStep,
                CONFIRMED_SET_NEXT_STEP_DEADLINE_STEPS,
            );
        }
        case "reopen": {
            if (currentStatus !== "confirmed" && currentStatus !== "closed") {
                return false;
            }
            return isAllowedDeadlineStep(nextDeadlineStep, OPEN_REQUEST_DEADLINE_STEPS);
        }
        default: {
            const _exhaustive: never = intent;
            return _exhaustive;
        }
    }
}

function isAllowedDeadlineStep(
    nextDeadlineStep: DeadlineStep | string | null | undefined,
    allowed: readonly string[],
): boolean {
    if (nextDeadlineStep == null) {
        // Omit step when checking menu-level affordances (action/intent only).
        return true;
    }
    return allowed.includes(nextDeadlineStep);
}

export type ChangeRequestStatusResolveError =
    | "invalid-status-action"
    | "offering-category-required"
    | "booking-outcome-required";

export type RequestStatusTransitionSubject = Pick<
    Request,
    "status" | "offeringCategory"
>;

export type RequestStatusChangeFields = {
    status?: RequestStatus;
    outcome?: RequestOutcome | null;
    bookingOutcome?: RequestBookingOutcome | null;
    nextDeadlineStep?: RequestDeadlineStep | null;
    nextDeadlineAt?: string | null;
    updateDeadline: boolean;
};

export function validateChangeRequestStatusTransition(
    body: ChangeRequestStatusRequest,
    existingRequest: RequestStatusTransitionSubject,
    bookings: RequestStatusBookingContext,
):
    | { fields: RequestStatusChangeFields; error?: undefined }
    | { fields?: undefined; error: ChangeRequestStatusResolveError } {
    if (
        !isStatusActionAllowed({
            action: body.action,
            currentStatus: existingRequest.status,
            bookings,
            intent: body.intent,
            nextDeadlineStep: body.nextDeadlineStep,
        })
    ) {
        return { error: "invalid-status-action" };
    }

    const resolved = resolveActionFields(body, existingRequest);
    if (resolved.error || !resolved.fields) {
        return resolved;
    }

    if (
        existingRequest.status === "draft" &&
        resolved.fields.status !== undefined &&
        resolved.fields.status !== "draft" &&
        !existingRequest.offeringCategory
    ) {
        return { error: "offering-category-required" };
    }

    return resolved;
}

function resolveActionFields(
    body: ChangeRequestStatusRequest,
    existingRequest: RequestStatusTransitionSubject,
):
    | { fields: RequestStatusChangeFields; error?: undefined }
    | { fields?: undefined; error: ChangeRequestStatusResolveError } {
    switch (body.action) {
        case "close":
            return {
                fields: {
                    status: "closed",
                    ...(body.outcome !== undefined && { outcome: body.outcome }),
                    updateDeadline: true,
                    nextDeadlineStep: null,
                    nextDeadlineAt: null,
                },
            };
        case "change_closed_resolution":
            if (body.outcome == null) {
                return { error: "invalid-status-action" };
            }
            return {
                fields: {
                    status: "closed",
                    outcome: body.outcome,
                    updateDeadline: true,
                    nextDeadlineStep: null,
                    nextDeadlineAt: null,
                },
            };
        case "change_booking_resolution":
            if (body.bookingOutcome == null) {
                return { error: "invalid-status-action" };
            }
            return {
                fields: {
                    bookingOutcome: body.bookingOutcome,
                    updateDeadline: false,
                },
            };
        case "confirm_no_further_action":
            if (
                existingRequest.status === "open" &&
                requiresBookingResolution(existingRequest.offeringCategory) &&
                body.bookingOutcome == null
            ) {
                return { error: "booking-outcome-required" };
            }
            return {
                fields: {
                    status: "confirmed",
                    outcome: "noActionNeeded",
                    ...(body.bookingOutcome !== undefined && {
                        bookingOutcome: body.bookingOutcome,
                    }),
                    updateDeadline: true,
                    nextDeadlineStep: null,
                    nextDeadlineAt: null,
                },
            };
        case "set_next_step": {
            if (
                body.intent == null ||
                body.nextDeadlineStep == null ||
                body.nextDeadlineAt == null
            ) {
                return { error: "invalid-status-action" };
            }

            if (
                body.intent === "confirm" &&
                requiresBookingResolution(existingRequest.offeringCategory) &&
                body.bookingOutcome == null
            ) {
                return { error: "booking-outcome-required" };
            }

            const status = resolveStatusForSetNextStepIntent(
                body.intent,
                existingRequest.status,
            );

            return {
                fields: {
                    status,
                    updateDeadline: true,
                    nextDeadlineStep: body.nextDeadlineStep,
                    nextDeadlineAt: body.nextDeadlineAt,
                    ...(body.intent === "reopen" && {
                        outcome: null,
                        bookingOutcome: null,
                    }),
                    ...(body.intent !== "reopen" &&
                        body.outcome !== undefined && { outcome: body.outcome }),
                    ...(body.intent !== "reopen" &&
                        body.bookingOutcome !== undefined && {
                            bookingOutcome: body.bookingOutcome,
                        }),
                },
            };
        }
        default: {
            const _exhaustive: never = body.action;
            void _exhaustive;
            return { error: "invalid-status-action" };
        }
    }
}
